import React from 'react';
import { Link } from 'react-router-dom';
import { EditorialNav, EditorialFooter } from './landing/EditorialShell';
import EditorialArticle from './landing/EditorialArticle';
import '../assets/scss/landing-v2.scss';

const TermsPage: React.FC = () => (
    <div
        className="lp2"
        style={{
            background: '#0a0a0a',
            color: '#f4f2ee',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <EditorialNav />
        <main style={{ flex: 1 }}>
            <EditorialArticle eyebrow="LEGAL" title="Terms of Use" subtitle="Effective date: March 25, 2026">
                <h2>1. Agreement to Terms</h2>
                <p>
                    These Terms of Use (&quot;Terms&quot;) constitute a legally binding agreement between you
                    (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) and{' '}
                    <strong>
                        Nazar Gryshchuk, conducting sole-trader business activity (Jednoosobowa Działalność
                        Gospodarcza, JDG) registered in Poland
                    </strong>
                    , operating under the brand <strong>&quot;DoYourStep&quot;</strong>{' '}
                    (&quot;Operator,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), governing your
                    access to and use of the website at doyourstep.com and all related services (collectively,
                    the &quot;Service&quot;).
                </p>
                <p>
                    By accessing or using the Service, you agree to be bound by these Terms and our{' '}
                    <Link to="/privacy">Privacy Policy</Link>. If you do not agree, you must not access or use
                    the Service.
                </p>

                <h2>2. Description of Service</h2>
                <p>
                    DoYourStep is a behavioral diagnostic platform that provides users with a self-assessment
                    tool designed to identify behavioral patterns across five functional dimensions (Energy,
                    Start, Process, Obstacles, and Recovery). Based on your responses, the Service generates a
                    personalized behavioral profile and a suggested protocol of micro-actions.
                </p>
                <p>
                    <strong>Important disclaimer:</strong> The Service is provided for informational and
                    self-development purposes only. It does not constitute medical, psychological, psychiatric,
                    or therapeutic advice, diagnosis, or treatment. The Service is not a substitute for
                    professional healthcare. Always consult a qualified professional for medical or mental
                    health concerns.
                </p>

                <h2>3. Eligibility</h2>
                <p>
                    You must be at least 18 years of age to use the Service. By using the Service, you
                    represent and warrant that you are at least 18 years old and have the legal capacity to
                    enter into these Terms.
                </p>

                <h2>4. User Accounts</h2>
                <p>To access certain features you may need to create an account. You agree to:</p>
                <ul>
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain the security and confidentiality of your login credentials</li>
                    <li>Accept responsibility for all activities that occur under your account</li>
                    <li>
                        Notify us immediately at support@doyourstep.com of any unauthorized use of your account
                    </li>
                </ul>
                <p>
                    We reserve the right to suspend or terminate your account if any information provided is
                    inaccurate, misleading, or violates these Terms.
                </p>

                <h2>5. Payments and Billing</h2>
                <h3>5.1 Payment Processing</h3>
                <p>
                    All payments for the Service are processed by <strong>Polar Software Inc.</strong>{' '}
                    (&quot;Polar&quot;), which acts as the Merchant of Record for all transactions. When you
                    make a purchase, you are buying from Polar, and Polar&apos;s terms and conditions apply
                    to the payment transaction. Polar handles all payment processing, invoicing, sales tax,
                    and VAT compliance on our behalf.
                </p>

                <h3>5.2 Pricing</h3>
                <p>
                    All prices are displayed in USD unless otherwise indicated. Prices are inclusive of
                    applicable taxes where required by law, which are calculated and collected by Polar. We
                    reserve the right to change our pricing at any time. Any price changes will not affect
                    existing paid orders.
                </p>

                <h3>5.3 Subscription and Free Trial</h3>
                <p>
                    The Service is offered as a monthly subscription that begins with a{' '}
                    <strong>7-day free trial</strong>. A valid payment method is required to start the trial.
                    If you do not cancel before the trial ends, your subscription will automatically begin and
                    your payment method will be charged <strong>$19.99 per month</strong> until you cancel.
                    You can cancel at any time - during the trial or afterwards - and you will retain access
                    until the end of the current billing period. Cancelling during the trial prevents any
                    charge.
                </p>

                <h2>6. Refund Policy</h2>
                <p>
                    We offer a <strong>30-day no-questions-asked refund guarantee</strong> from the date of
                    purchase. If you are unsatisfied with the Service for any reason, you may request a full
                    refund within 30 calendar days of your purchase by contacting us at
                    support@doyourstep.com.
                </p>
                <p>
                    Refund requests made after the 30-day period will not be accepted. Refunds are processed by
                    Polar (Merchant of Record) and will be returned to the original payment method within
                    5-10 business days.
                </p>
                <p>
                    We reserve the right to deny refund requests where we detect abuse, fraud, or repeated
                    purchase-and-refund behavior. The full refund policy is described on the dedicated{' '}
                    <Link to="/refund">Refund Policy</Link> page.
                </p>

                <h2>7. Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul>
                    <li>
                        Use the Service for any unlawful purpose or in violation of any applicable local, state,
                        national, or international law
                    </li>
                    <li>
                        Attempt to reverse-engineer, decompile, disassemble, or otherwise derive the source code
                        of the Service
                    </li>
                    <li>Scrape, crawl, or use automated tools to access or extract data from the Service</li>
                    <li>
                        Interfere with or disrupt the integrity or performance of the Service or its underlying
                        infrastructure
                    </li>
                    <li>
                        Attempt to gain unauthorized access to any systems or networks connected to the Service
                    </li>
                    <li>
                        Resell, redistribute, or sublicense the Service or any content generated by it without
                        prior written consent
                    </li>
                    <li>Use the Service to harass, abuse, or harm any person</li>
                </ul>

                <h2>8. Intellectual Property</h2>
                <p>
                    All content, features, functionality, design elements, trademarks, logos, and software
                    associated with the Service are and shall remain the exclusive property of DoYourStep or its
                    licensors. This includes, but is not limited to, the diagnostic methodology, question sets,
                    scoring algorithms, report templates, and protocol frameworks.
                </p>
                <p>
                    You are granted a limited, non-exclusive, non-transferable, revocable license to access and
                    use the Service for personal, non-commercial purposes in accordance with these Terms. You
                    may not copy, modify, distribute, sell, or lease any part of the Service without our prior
                    written permission.
                </p>

                <h2>9. User-Generated Data</h2>
                <p>
                    Your diagnostic responses and resulting profile are personal to you. We do not sell your
                    individual data. We may use anonymized, aggregated data derived from user responses for
                    research, product improvement, and statistical analysis purposes. For full details, see our{' '}
                    <Link to="/privacy">Privacy Policy</Link>.
                </p>

                <h2>10. Assumption of Risk</h2>
                <p>BY USING THE SERVICE, YOU ACKNOWLEDGE AND AGREE THAT:</p>
                <ul>
                    <li>
                        The Service provides informational content for self-development purposes only and is not
                        a substitute for professional medical, psychological, psychiatric, or therapeutic advice
                    </li>
                    <li>
                        You are solely responsible for any decisions or actions you take based on the diagnostic
                        results, behavioral profile, or protocol provided by the Service
                    </li>
                    <li>You use the Service entirely at your own risk and discretion</li>
                    <li>
                        DoYourStep bears no responsibility for any outcomes, consequences, or results arising
                        from your use of the Service or reliance on its content
                    </li>
                    <li>
                        You will not hold DoYourStep liable for any personal, professional, financial,
                        health-related, or other consequences resulting from your use of the Service
                    </li>
                </ul>

                <h2>11. Disclaimer of Warranties</h2>
                <p>
                    THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS WITHOUT
                    WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
                    WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR
                    ACCURACY.
                </p>
                <p>
                    We do not warrant that the Service will be uninterrupted, error-free, or secure. We do not
                    guarantee the accuracy, completeness, or usefulness of any behavioral profile, diagnostic
                    result, or protocol generated by the Service.
                </p>
                <p>
                    The behavioral diagnostic and protocol are based on established behavioral science
                    frameworks but are not clinically validated diagnostic tools. Results should be used as a
                    self-awareness aid, not as a clinical assessment.
                </p>

                <h2>12. Limitation of Liability</h2>
                <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL DOYOURSTEP, ITS
                    OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS
                    OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION
                    WITH:
                </p>
                <ul>
                    <li>Your access to, use of, or inability to use the Service</li>
                    <li>Any conduct or content of any third party on the Service</li>
                    <li>Any content obtained from the Service</li>
                    <li>Any decisions made or actions taken based on your diagnostic results or protocols</li>
                </ul>
                <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, DOYOURSTEP&apos;S TOTAL CUMULATIVE
                    LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE
                    AMOUNT ACTUALLY PAID BY YOU TO DOYOURSTEP FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM.
                </p>

                <h2>13. Indemnification</h2>
                <p>
                    You agree to defend, indemnify, and hold harmless DoYourStep and its officers, directors,
                    employees, agents, and affiliates from and against any claims, liabilities, damages, losses,
                    and expenses (including reasonable attorneys&apos; fees) arising out of or in any way
                    connected with your access to or use of the Service, your violation of these Terms, or your
                    violation of any third-party rights.
                </p>

                <h2>14. Dispute Resolution</h2>
                <h3>14.1 Informal Resolution</h3>
                <p>
                    Before filing any formal claim, you agree to first contact us at support@doyourstep.com and
                    attempt to resolve the dispute informally for at least thirty (30) days.
                </p>
                <h3>14.2 Governing Law</h3>
                <p>
                    These Terms shall be governed by and construed in accordance with the laws of the State of
                    Delaware, United States, without regard to its conflict of law provisions.
                </p>
                <h3>14.3 Jurisdiction</h3>
                <p>
                    Any disputes arising out of or relating to these Terms that cannot be resolved informally
                    shall be subject to the exclusive jurisdiction of the courts located in the State of
                    Delaware, United States. You consent to the personal jurisdiction of such courts.
                </p>

                <h2>15. Termination</h2>
                <p>
                    We may terminate or suspend your access to the Service immediately, without prior notice or
                    liability, for any reason, including but not limited to a breach of these Terms. Upon
                    termination, your right to use the Service will immediately cease.
                </p>
                <p>
                    All provisions of these Terms which by their nature should survive termination shall
                    survive, including but not limited to ownership provisions, warranty disclaimers, indemnity,
                    and limitation of liability.
                </p>

                <h2>16. Changes to Terms</h2>
                <p>
                    We reserve the right to modify these Terms at any time. If we make material changes, we will
                    notify you by updating the &quot;Effective date&quot; at the top of this page and, where
                    practical, by sending an email notification. Your continued use of the Service after any
                    changes constitutes your acceptance of the revised Terms.
                </p>

                <h2>17. Severability</h2>
                <p>
                    If any provision of these Terms is held to be invalid, illegal, or unenforceable, the
                    remaining provisions shall continue in full force and effect.
                </p>

                <h2>18. Entire Agreement</h2>
                <p>
                    These Terms, together with the <Link to="/privacy">Privacy Policy</Link>, constitute the
                    entire agreement between you and DoYourStep regarding the Service and supersede all prior
                    agreements and understandings.
                </p>

                <h2>19. Contact</h2>
                <p>If you have any questions about these Terms, please contact us:</p>
                <p>
                    <strong>Email:</strong> support@doyourstep.com
                </p>
            </EditorialArticle>
        </main>
        <EditorialFooter />
    </div>
);

export default TermsPage;
